import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { generateCodeVerifier, generateCodeChallenge } from '../utils/pkce';
import { AUTH_CONFIG, getAuthorizationEndpoint } from '../utils/authConfig';

type UserProfile = Record<string, unknown> & {
    name?: string;
    given_name?: string;
    family_name?: string;
    sub?: string;
    access_token?: string;
    refresh_token?: string;
    id_token?: string;
    expires_in?: number;
    token_type?: string;
    scope?: string;
};

type AuthContextValue = {
    user: UserProfile | null;
    isAuthenticating: boolean;
    loginError: string | null;
    login: () => Promise<void>;
    completeLogin: (profile: UserProfile) => void;
    logout: () => Promise<void>;
};

const AUTH_USER_STORAGE_KEY = 'koala.sso.user';
export const STATE_STORAGE_KEY = 'koala.sso.state';
const CODE_VERIFIER_STORAGE_KEY = 'koala.sso.code_verifier';
const CODE_CHALLENGE_STORAGE_KEY = 'koala.sso.code_challenge';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const isBrowser = typeof window !== 'undefined';

const getStoredUser = (): UserProfile | null => {
    if (!isBrowser) return null;
    const raw = sessionStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw) as UserProfile;
    } catch {
        sessionStorage.removeItem(AUTH_USER_STORAGE_KEY);
        return null;
    }
};

const setStateValue = (value: string): void => {
    if (!isBrowser) return;
    try {
        sessionStorage.setItem(STATE_STORAGE_KEY, value);
    } catch {
        // ignore
    }
    try {
        localStorage.setItem(STATE_STORAGE_KEY, value);
    } catch {
        // ignore
    }
};

const getStateValue = (): string | null => {
    if (!isBrowser) return null;
    try {
        const sessionValue = sessionStorage.getItem(STATE_STORAGE_KEY);
        if (sessionValue) {
            return sessionValue;
        }
    } catch {
        // ignore
    }
    try {
        return localStorage.getItem(STATE_STORAGE_KEY);
    } catch {
        return null;
    }
};

const clearStateValue = (): void => {
    if (!isBrowser) return;
    try {
        sessionStorage.removeItem(STATE_STORAGE_KEY);
    } catch {
        // ignore
    }
    try {
        localStorage.removeItem(STATE_STORAGE_KEY);
    } catch {
        // ignore
    }
};

const setCodeVerifier = (value: string): void => {
    if (!isBrowser) return;
    try {
        sessionStorage.setItem(CODE_VERIFIER_STORAGE_KEY, value);
        // Verify it was stored correctly
        const stored = sessionStorage.getItem(CODE_VERIFIER_STORAGE_KEY);
        if (stored !== value) {
            console.warn('[AuthContext] Code verifier storage mismatch!', {
                originalLength: value.length,
                storedLength: stored?.length,
            });
        }
    } catch (error) {
        console.error('[AuthContext] Failed to store code verifier', error);
    }
};

const getCodeVerifier = (): string | null => {
    if (!isBrowser) return null;
    try {
        return sessionStorage.getItem(CODE_VERIFIER_STORAGE_KEY);
    } catch {
        return null;
    }
};

const clearCodeVerifier = (): void => {
    if (!isBrowser) return;
    try {
        sessionStorage.removeItem(CODE_VERIFIER_STORAGE_KEY);
        sessionStorage.removeItem(CODE_CHALLENGE_STORAGE_KEY);
    } catch {
        // ignore
    }
};

export const getStoredState = (): string | null => getStateValue();
export const getStoredCodeVerifier = (): string | null => getCodeVerifier();

type Props = {
    children: ReactNode;
};

export const AuthProvider = ({ children }: Props): JSX.Element => {
    const [user, setUser] = useState<UserProfile | null>(() => getStoredUser());
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);

    const login = useCallback(async () => {
        setIsAuthenticating(true);
        setLoginError(null);
        try {
            // Generate PKCE code verifier and challenge
            // Use 64 bytes to ensure base64url output is well under RFC 7636's 128-char limit
            const codeVerifier = generateCodeVerifier(64);
            const codeChallenge = await generateCodeChallenge(codeVerifier);

            // Generate random state for CSRF protection
            const state = generateCodeVerifier(32);

            // Store code verifier, challenge, and state for later verification
            setCodeVerifier(codeVerifier);
            setStateValue(state);

            // Store code_challenge for comparison later
            if (isBrowser) {
                try {
                    sessionStorage.setItem(CODE_CHALLENGE_STORAGE_KEY, codeChallenge);
                } catch (error) {
                    console.error('[AuthContext] Failed to store code challenge', error);
                }
            }

            // Verify storage immediately
            const storedVerifierCheck = sessionStorage.getItem(CODE_VERIFIER_STORAGE_KEY);
            const storedChallengeCheck = sessionStorage.getItem(CODE_CHALLENGE_STORAGE_KEY);
            if (storedVerifierCheck !== codeVerifier) {
                console.error('[AuthContext] Code verifier storage mismatch immediately after storing!', {
                    original: codeVerifier.substring(0, 50),
                    stored: storedVerifierCheck?.substring(0, 50),
                });
            }
            if (storedChallengeCheck !== codeChallenge) {
                console.error('[AuthContext] Code challenge storage mismatch immediately after storing!', {
                    original: codeChallenge.substring(0, 30),
                    stored: storedChallengeCheck?.substring(0, 30),
                });
            }

            // Build authorization URL with PKCE
            // Use URLSearchParams for ALL parameters to ensure consistent encoding
            const baseUrl = getAuthorizationEndpoint();
            const params = new URLSearchParams();

            // Add all parameters using URLSearchParams to prevent encoding issues
            params.append('client_id', AUTH_CONFIG.CLIENT_ID);
            params.append('redirect_uri', AUTH_CONFIG.REDIRECT_URI);
            params.append('response_type', 'code');
            params.append('scope', AUTH_CONFIG.SCOPES);
            params.append('prompt', 'login');
            params.append('audience', AUTH_CONFIG.KEYCLOAK_AUDIENCE);
            params.append('state', state);
            params.append('code_challenge', codeChallenge);
            params.append('code_challenge_method', 'S256');
            params.append('response_mode', 'fragment');

            const authUrl = `${baseUrl}?${params.toString()}`;

            // Extract the code_challenge from the URL to verify it was encoded correctly
            const urlObj = new URL(authUrl);
            const codeChallengeInUrl = urlObj.searchParams.get('code_challenge');
            const challengeMatches = codeChallengeInUrl === codeChallenge;

            if (!challengeMatches) {
                console.error('[AuthContext] CRITICAL: Code challenge was modified when constructing URL!', {
                    original: codeChallenge,
                    inUrl: codeChallengeInUrl,
                    originalLength: codeChallenge.length,
                    inUrlLength: codeChallengeInUrl?.length,
                    fullUrl: authUrl.substring(0, 500), // Log first 500 chars to avoid huge logs
                });
            }

            window.location.assign(authUrl);
        } catch (error) {
            setIsAuthenticating(false);
            const message = error instanceof Error ? error.message : 'Unable to authenticate.';
            setLoginError(message);
        }
    }, []);

    const completeLogin = useCallback((profile: UserProfile) => {
        clearStateValue();
        clearCodeVerifier();
        sessionStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(profile));
        setUser(profile);
        setIsAuthenticating(false);
        setLoginError(null);
    }, []);

    const logout = useCallback(async () => {
        setLoginError(null);
        setIsAuthenticating(true);
        try {
            // Clear local storage - no backend call needed
            // The access token cookie (if any) will expire naturally
        } finally {
            sessionStorage.removeItem(AUTH_USER_STORAGE_KEY);
            clearStateValue();
            clearCodeVerifier();
            setUser(null);
            setIsAuthenticating(false);
        }
    }, []);

    const value = useMemo(
        () => ({
            user,
            isAuthenticating,
            loginError,
            login,
            completeLogin,
            logout,
        }),
        [user, isAuthenticating, loginError, login, completeLogin, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
